import React from 'react'
import { hasRequestedRatingSemantics } from '@digital-twins/shared-types'

import type {
  ChartSegmentIdLabel,
  ChartSegmentInnerView,
  ChartSegmentSlideType,
  ChartSegmentView,
  ChartSegmentViewRequest,
  RatingStatistics,
  ResolvedChartSegmentDefault,
} from '@digital-twins/shared-types'

import { CHART_SEGMENT_CONTROL_CLEARANCE_X, ChartSegmentControl, ChartSegmentSelection } from './ChartSegmentControl'
import { useChartSegmentContext } from './ChartSegmentContext'
import type { ChartSegmentLabels } from './ChartSegmentContext'
import { HeatGridPlot } from '../../plots/heat-grid/HeatGridPlot'
import { readStampedHeatGrid } from '../../plots/heat-grid/readAdapters'
import { ChartSegmentTabs } from './ChartSegmentTabs'
import { TextClamp } from '../text/TextClamp'
import type { ChartSegmentFigureRenderer } from './ChartSegmentTabs'
import { CHART_SEGMENT_GEAR_CLEARANCE, CHART_SEGMENT_GEAR_INSET } from './gear-layout'
import { hasChartSegmentTrace, inferQuestionIdFromSlideId, readSlideTrace } from './readSlideTrace'
import { usePublishChartSegmentSelection } from './selection-scope'

interface SegmentHeatmapProps {
  view: Extract<ChartSegmentView, { kind: 'heatmap' }>
  title?: React.ReactNode
}

/**
 * The gear's segments-as-rows view: the heat-grid Python stamped on it
 * (`view.plot`, plots plan Q). Segment views are requested live, never
 * stored, and Python stamps every view with columns (W6-G), so a view
 * without its plot draws nothing.
 */
function SegmentHeatmap(props: SegmentHeatmapProps) {
  const { view, title } = props

  const stamped = readStampedHeatGrid((view as { plot?: unknown }).plot)

  if (!stamped) {
    return null
  }
  // The gear's own insets, as today's heatmap takes them: the title keeps
  // clear of the control at the top right.
  // The chart's own title, clamped to two lines as the tabbed chrome clamps it.
  const heading = title ? (
    <span style={{ display: 'block', paddingRight: CHART_SEGMENT_CONTROL_CLEARANCE_X - CHART_SEGMENT_GEAR_CLEARANCE }}>
      <TextClamp
        data-testid="chart-segment-title"
        minLines={1}
        maxLines={2}
        lineHeight="1.2"
        style={{ fontSize: 16, fontWeight: 600, color: 'var(--slide-text-primary)' }}
      >
        {title}
      </TextClamp>
    </span>
  ) : null
  return (
    <div
      data-testid="chart-segment-heat-grid-view"
      style={{
        height: '100%',
        minHeight: 0,
        padding: `${CHART_SEGMENT_GEAR_INSET}px ${CHART_SEGMENT_GEAR_CLEARANCE}px 16px 20px`,
        boxSizing: 'border-box',
      }}
    >
      <HeatGridPlot
        data={stamped.data}
        // Each segment row names its base, as the gear always has (a
        // producer's own `showRowN` wins).
        props={{ showRowN: true, ...stamped.props }}
        title={heading}
        testIdPrefix="chart-segment-heat-grid"
        // The capped-groups note is the view's, not the plot's: it says which
        // groups the cut left out, which the grid cannot show.
        footer={
          view.overflowNote ? (
            <div data-testid="chart-segment-overflow-note" style={{ fontSize: 11, color: 'var(--slide-text-muted)' }}>
              {view.overflowNote}
            </div>
          ) : undefined
        }
      />
    </div>
  )
}

interface PlotBoxProps {
  children: React.ReactNode
  slide: Record<string, unknown>
  slideType: ChartSegmentSlideType
  columns: Array<ChartSegmentIdLabel>
  rows?: Array<ChartSegmentIdLabel>
  categories?: Array<ChartSegmentIdLabel>
  displayLabels?: Record<string, string>
  title?: React.ReactNode
  /** Gear inset from the panel's top edge; see chartSegmentGearInsetTop. */
  gearInsetTop?: number
  /**
   * The figure's colour key, for slides whose chart is unreadable without one. Drawn
   * in the tabbed chrome's title row, where the baked chart already draws it.
   */
  segmentLegend?: React.ReactNode
  /**
   * Lets the slide draw one segment with its own chart rather than the generic inner
   * view, so a breakdown keeps the baked figure's presence instead of shrinking to a
   * stripe. Return null for the kinds the slide does not handle.
   */
  renderSegmentFigure?: ChartSegmentFigureRenderer
  /**
   * The slide draws its chart split by a cut, so Off returns to that split: the
   * gear's Off option says so, in the host's words (`labels.offShowsSplit`).
   */
  showsSplit?: boolean
  /** The native host draws the recorded source without constructing a live view. */
  renderRecordedFigure?: (source: ResolvedChartSegmentDefault) => React.ReactNode
}

function isBlankInnerView(view: ChartSegmentInnerView): boolean {
  function hasAnsweredValue(value?: number | null): boolean {
    return typeof value === 'number' && Number.isFinite(value) && value !== 0
  }
  if (view.kind === 'heatmap') {
    return !view.rows.some((row) => row.values.some(hasAnsweredValue))
  }
  if (view.kind === 'likert-diverging') {
    if (view.rows?.length) {
      return view.rows.every(
        (row) =>
          !row.buckets.some((bucket) => hasAnsweredValue(bucket.value)) &&
          !(row.segments || []).some((segment) => hasAnsweredValue(segment.value)),
      )
    }
    return !view.buckets.some((bucket) => hasAnsweredValue(bucket.value))
  }
  if (view.kind === 'matrix-rating') {
    return !view.rows.some((row) => row.cells.some((cell) => hasAnsweredValue(cell.value)))
  }
  if (view.kind === 'quadrant') {
    // 0 is a real corner of the map here, so a plottable point is one that has
    // both coordinates — not one whose coordinates are non-zero.
    return !view.points.some((point) => point.x !== null && point.y !== null)
  }
  return !view.rows.some((row) => row.bars.some((bar) => hasAnsweredValue(bar.value)))
}

function isBlankSegmentView(view: ChartSegmentView): boolean {
  if (view.kind === 'heatmap') {
    if (view.rows.length === 0) {
      return true
    }
    return view.rows.every((row) => (row.values || []).every((value) => value == null))
  }
  return (
    view.tabs.length === 0 ||
    view.tabs.every((tab) => tab.n <= 0 && isBlankInnerView(tab.view))
  )
}

function blankSegmentMessage(
  axisId: string,
  axes: Array<{ id: string; label: string }>,
  labels: ChartSegmentLabels,
): string {
  return labels.noBreakdown(axes.find((axis) => axis.id === axisId)?.label || labels.thisSegment)
}

export function PlotBox(props: PlotBoxProps) {
  const trace = readSlideTrace(props.slide)
  return <PlotBoxBody key={`${trace.slideId}:${trace.chartSegmentDefault?.savedAudienceSnapshotDigest ?? ''}:${trace.chartSegmentDefault?.sourcePlotDigest ?? ''}`} {...props} />
}

function PlotBoxBody(props: PlotBoxProps) {
  const {
    children,
    slide,
    slideType,
    columns,
    rows,
    categories,
    displayLabels,
    title,
    gearInsetTop,
    segmentLegend,
    renderSegmentFigure,
    showsSplit = false,
    renderRecordedFigure,
  } = props
  const context = useChartSegmentContext()
  const trace = readSlideTrace(slide)
  const recorded = trace.chartSegmentDefault
  if (recorded && (!renderRecordedFigure || showsSplit ||
      recorded.slideType !== (slideType === 'single-select-bars' ? 'single_select_choice_bars' :
        slideType === 'multi-select-bars' ? 'multi_select_choice_bars' : null))) {
    throw new Error('Recorded chart source requires its admitted native choice host.')
  }
  const axes = context?.meta?.axes || []
  const [isRecordedInitial, setIsRecordedInitial] = React.useState(Boolean(recorded))
  const [selection, setSelection] = React.useState<ChartSegmentSelection>(() => recorded
    ? { id: JSON.stringify([recorded.selector.axisId, recorded.figureGroupId]),
        axisId: recorded.selector.axisId, levelIds: recorded.selector.levelIds }
    : { id: 'off', axisId: 'off' })
  const axisId = selection.axisId
  const metadataPending = context?.meta == null
  const selectedAxis = axes.find((axis) => axis.id === axisId)
  const isSelectedLevelAvailable =
    selectedAxis?.kind !== 'overlapping' ||
    Boolean(
      selection.levelIds?.length &&
      selection.levelIds.every((levelId) => selectedAxis.levels.some((level) => level.id === levelId)),
    )
  const isSelectionAvailable =
    isRecordedInitial || metadataPending || axisId === 'off' || Boolean(selectedAxis && isSelectedLevelAvailable)
  const [view, setView] = React.useState<ChartSegmentView | null>(null)
  const [hasFetchError, setHasFetchError] = React.useState(false)
  const [retryRevision, setRetryRevision] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const abortRef = React.useRef<AbortController | null>(null)
  const requestKeyRef = React.useRef('')
  const showBaked = isRecordedInitial || axisId === 'off'
  const getView = context?.getView
  const tagsRevision = context?.meta?.tagsRevision || ''
  const requestSignature = JSON.stringify({
    ratingSemanticsVersion: (slide.ratingStatistics as RatingStatistics | undefined)?.policyVersion,
    slideType,
    questionId: trace.questionId || inferQuestionIdFromSlideId(trace.slideId) || '',
    metricId: trace.metricId || '',
    assetId: trace.assetId || '',
    columns,
    rows,
    categories,
    levelIds: selection.levelIds,
  })

  // An axis-load failure must not silently delete the control: that reads as the
  // feature being gone rather than a service being briefly unavailable.
  const axesUnavailable = context?.meta?.axesUnavailable === true
  const canShow = Boolean(
    context &&
    context.meta &&
    (axes.length > 0 || axesUnavailable || recorded) &&
    hasChartSegmentTrace(slide),
  )

  React.useEffect(() => {
    if (selection.axisId !== 'off' && !isSelectionAvailable) {
      abortRef.current?.abort()
      requestKeyRef.current = ''
      setView(null)
      setHasFetchError(false)
      setLoading(false)
      setSelection({ id: 'off', axisId: 'off' })
    }
  }, [isSelectionAvailable, selection])

  React.useEffect(() => {
    // Recorded initial facts never depend on current tag metadata or a provider.
    if (showBaked) {
      abortRef.current?.abort()
      abortRef.current = null
      setView(null)
      setHasFetchError(false)
      setLoading(false)
      return
    }
    // A report refresh temporarily clears metadata. Keep the reader's selected
    // axis while it loads, but never display or request facts from that gap.
    if (metadataPending) {
      abortRef.current?.abort()
      setView(null)
      setHasFetchError(false)
      setLoading(!showBaked)
      return
    }
    if (!isSelectionAvailable) {
      abortRef.current?.abort()
      return
    }
    const slideId = trace.slideId
    if (!getView || !slideId) {
      return
    }
    const key = `${slideId}:${axisId}:${tagsRevision}:${requestSignature}`
    if (requestKeyRef.current === key) {
      return
    }
    requestKeyRef.current = key
    const controller = new AbortController()
    abortRef.current?.abort()
    abortRef.current = controller
    const request = JSON.parse(requestSignature) as ChartSegmentViewRequest
    request.axisId = axisId
    if (!request.metricId) {
      delete request.metricId
    }
    if (!request.assetId) {
      delete request.assetId
    }
    setLoading(true)
    setHasFetchError(false)
    void getView(slideId, request, controller.signal)
      .then((next) => {
        if (!hasRequestedRatingSemantics(next, request.ratingSemanticsVersion)) {
          throw new Error('The analysis service returned incompatible rating data.')
        }
        if (requestKeyRef.current !== key || controller.signal.aborted) {
          return
        }
        setView(next)
        setHasFetchError(false)
        setLoading(false)
      })
      .catch(() => {
        if (requestKeyRef.current !== key || controller.signal.aborted) {
          return
        }
        setView(null)
        setHasFetchError(true)
        setLoading(false)
      })
    return () => {
      controller.abort()
      if (requestKeyRef.current === key) {
        requestKeyRef.current = ''
      }
    }
  }, [axisId, getView, isSelectionAvailable, metadataPending, requestSignature, retryRevision, showBaked, tagsRevision, trace.slideId])

  const handleSelect = (nextSelection: ChartSegmentSelection) => {
    if (!isRecordedInitial && nextSelection.id === selection.id) {
      return
    }
    abortRef.current?.abort()
    requestKeyRef.current = ''
    setHasFetchError(false)
    setIsRecordedInitial(false)
    setSelection(nextSelection)
    if (nextSelection.axisId === 'off') {
      setView(null)
      setLoading(false)
      return
    }
    setView(null)
    setLoading(true)
  }

  function handleRetry() {
    requestKeyRef.current = ''
    setHasFetchError(false)
    setView(null)
    setLoading(true)
    setRetryRevision((current) => current + 1)
  }

  const filled = !showBaked && (loading || hasFetchError || view != null)

  // Tell the copy beside this chart what the chart is actually DRAWING.
  //
  // Not what the reader SELECTED: a cut can be chosen while the panel still shows
  // a spinner, a retry prompt, or "nobody in this segment answered". Disclaiming
  // the narrative in those states would tell the reader their figures are suspect
  // at the exact moments there is nothing on screen to compare them against.
  const publishSelection = usePublishChartSegmentSelection()
  const showingSegmentNumbers =
    isRecordedInitial || (!showBaked && !loading && !hasFetchError && view != null && !isBlankSegmentView(view))
  const displayedAxisLabel = isRecordedInitial ? recorded?.groupContext.label : selectedAxis?.label
  React.useEffect(() => {
    publishSelection(
      showingSegmentNumbers
        ? { isSegmented: true, axisId, axisLabel: displayedAxisLabel }
        : { isSegmented: false },
    )
  }, [axisId, publishSelection, displayedAxisLabel, showingSegmentNumbers])
  // Separate from the effect above so its cleanup fires on unmount ONLY. Folded
  // into that one it would publish false-then-true on every dependency change,
  // flickering the disclaimer each time the chart re-fetched.
  React.useEffect(() => () => publishSelection({ isSegmented: false }), [publishSelection])

  return (
    <div
      data-testid="chart-segment-plot-box"
      data-filled={filled ? 'true' : 'false'}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: filled ? '100%' : 0,
      }}
    >
      {context && canShow ? (
        <ChartSegmentControl
          axes={axes}
          selectedOptionId={selection.id}
          onSelect={handleSelect}
          offLabel={showsSplit ? context.labels.offShowsSplit : context.labels.offWholeSample}
          labels={context.labels}
          insetTop={gearInsetTop}
          axesUnavailable={axesUnavailable}
          onRetry={() => {
            void context.refreshMeta()
          }}
          onOpen={() => {
            void context.refreshMeta()
          }}
        />
      ) : null}
      {context && axisId !== 'off' && !showBaked && loading ? (
        <div
          data-testid="chart-segment-skeleton"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'var(--slide-card-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            color: 'var(--slide-text-muted)',
            minHeight: '100%',
          }}
        >
          {context.labels.loading}
        </div>
      ) : null}
      {/* The recorded source is static; manual views use the current provider. */}
      {isRecordedInitial && recorded && renderRecordedFigure ? renderRecordedFigure(recorded) : !showBaked && context ? (
        !loading && hasFetchError ? (
          <div
            data-testid="chart-segment-error"
            role="alert"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              gap: 12,
              height: '100%',
              padding: `${CHART_SEGMENT_GEAR_CLEARANCE}px ${CHART_SEGMENT_GEAR_CLEARANCE}px 24px 24px`,
              boxSizing: 'border-box',
              color: 'var(--slide-text-secondary)',
              fontSize: 14,
            }}
          >
            <span>{context.labels.viewFailed}</span>
            <button
              type="button"
              data-testid="chart-segment-retry-button"
              onClick={handleRetry}
              style={{
                minHeight: 44,
                padding: '8px 14px',
                border: '1px solid var(--slide-chart-segment-control-border, #d7d7d7)',
                borderRadius: 8,
                background: 'var(--slide-chart-segment-control-bg, var(--slide-bg, #ffffff))',
                color: 'var(--slide-chart-segment-control-fg, var(--slide-text-secondary, #384242))',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {context.labels.retry}
            </button>
          </div>
        ) : !loading && view ? (
          isBlankSegmentView(view) ? (
            <div
              data-testid="chart-segment-empty"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                minHeight: 0,
                padding: `${CHART_SEGMENT_GEAR_CLEARANCE}px ${CHART_SEGMENT_GEAR_CLEARANCE}px 24px 24px`,
                boxSizing: 'border-box',
                fontSize: 14,
                lineHeight: 1.5,
                color: 'var(--slide-text-secondary)',
              }}
            >
              {view.emptyReason || blankSegmentMessage(axisId, context.meta?.axes || [], context.labels)}
            </div>
          ) : view.kind === 'heatmap' ? (
            <SegmentHeatmap view={view} title={title} />
          ) : (
            <ChartSegmentTabs
              view={view}
              displayLabels={displayLabels}
              title={title}
              renderFigure={renderSegmentFigure}
              legend={segmentLegend}
              // The tabs chrome lays itself out around the control, so it has
              // to be told the same inset the control was given.
              gearInsetTop={gearInsetTop}
              labels={context.labels}
            />
          )
        ) : null
      ) : (
        children
      )}
    </div>
  )
}
